import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  featureMessage,
  limitMessage,
  PLAN_ERROR_PREFIX,
  planFor,
  type AddonId,
  type FeatureKey,
  type LimitKey,
  type Limits,
  type PlanId,
  type Usage,
} from '@machs/shared';
import { api } from './api';
import { useSync } from './sync';

// Tarif des Teams für Hinweise vorab. Der Server prüft ohnehin selbst; hier
// geht es darum, dass niemand erst nach dem Absenden merkt, dass etwas fehlt.

export interface PlanInfo {
  plan: PlanId;
  paidPlan: PlanId;
  trial: boolean;
  trialEndsAt: string | null;
  addons: AddonId[];
  extraSeats: number;
  interval: 'month' | 'year' | null;
  limits: Limits;
  features: FeatureKey[];
  hasSubscription: boolean;
  usage: Usage;
  billing: { ready: boolean; canManage: boolean };
}

/** Warum der Hinweis kommt: Text und kleinster passender Tarif */
export interface UpgradeReason {
  message: string;
  needs: PlanId | null;
}

interface PlanCtx {
  info: PlanInfo | null;
  refresh: () => void;
  /** Grenze prüfen; bei Überschreitung öffnet sich der Hinweis und es kommt false */
  allow: (key: LimitKey, next: number) => boolean;
  /** Kurzform: ist noch eins mehr erlaubt? (Projekte, Aufgaben, Freigaben …) */
  more: (key: Exclude<LimitKey, 'storageMb' | 'videoSeconds' | 'boardsPerProject'>) => boolean;
  allowFeature: (feature: FeatureKey) => boolean;
  /** Speicher und Videolänge vor einem Upload */
  allowUpload: (bytes: number, videoSeconds?: number | null) => boolean;
  /** Hinweis direkt öffnen (z. B. nach einer Ablehnung durch den Server) */
  showUpgrade: (reason?: UpgradeReason) => void;
  reason: UpgradeReason | null | undefined;
  closeUpgrade: () => void;
}

const Ctx = createContext<PlanCtx | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { client, version, me } = useSync();
  const [info, setInfo] = useState<PlanInfo | null>(null);
  // undefined = zu, null = offen ohne bestimmten Anlass (Tarife vergleichen)
  const [reason, setReason] = useState<UpgradeReason | null | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    if (me.guest) return;
    void api<PlanInfo>('/billing/plan').then(setInfo, () => {});
  }, [me.guest]);

  useEffect(() => refresh(), [refresh]);
  // Nach Änderungen (Uploads, neue Projekte) die Nutzung gebündelt nachladen
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(refresh, 4000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [version, refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);
  // Hat der Server eine Änderung an einer Tarifgrenze abgelehnt (etwa zwei Geräte
  // gleichzeitig), kommt derselbe Hinweis
  const seenRejected = useRef(0);
  useEffect(() => {
    const list = client.rejected;
    const fresh = list.slice(seenRejected.current);
    seenRejected.current = list.length;
    const hit = fresh.find((r) => r.error.startsWith(PLAN_ERROR_PREFIX));
    if (hit) setReason({ message: hit.error.slice(PLAN_ERROR_PREFIX.length).trim(), needs: null });
  }, [client, version]);

  // Zurück aus Stripe: Tarif neu laden
  useEffect(() => {
    const url = new URL(location.href);
    if (!url.searchParams.has('abo')) return;
    url.searchParams.delete('abo');
    history.replaceState(null, '', `${url.pathname}${url.search}`);
    setTimeout(refresh, 1500);
  }, [refresh]);

  /** Anzahlen sofort aus den lokalen Daten, damit Hinweise ohne Verzögerung stimmen */
  const local = useMemo(() => {
    let projects = 0;
    for (const p of client.data.project.values()) if (!p.archivedAt && !p.isInternal) projects++;
    let openTasks = 0;
    for (const t of client.data.task.values()) if (t.status !== 'erledigt') openTasks++;
    let businesses = 0;
    for (const b of client.data.business.values()) if (!b.archivedAt) businesses++;
    return { projects, openTasks, businesses };
  }, [client, version]);

  const value = useMemo<PlanCtx>(() => {
    const usageOf = (key: LimitKey): number => {
      if (key === 'projects' || key === 'openTasks' || key === 'businesses') return local[key];
      return info?.usage[key as keyof Usage] ?? 0;
    };
    const allow = (key: LimitKey, next: number) => {
      if (!info) return true;
      const max = info.limits[key];
      if (max === null || next <= max) return true;
      setReason({ message: limitMessage(info.plan, key, info.limits), needs: planFor({ limit: key, wanted: next }) });
      return false;
    };
    return {
      info: info && { ...info, usage: { ...info.usage, ...local } },
      refresh,
      allow,
      more: (key) => allow(key, usageOf(key) + 1),
      allowFeature: (feature) => {
        if (!info || info.features.includes(feature)) return true;
        setReason({ message: featureMessage(info.plan, feature), needs: planFor({ feature }) });
        return false;
      },
      allowUpload: (bytes, videoSeconds) => {
        if (!info) return true;
        if (videoSeconds && !allow('videoSeconds', Math.floor(videoSeconds))) return false;
        return allow('storageMb', usageOf('storageMb') + bytes / 1024 / 1024);
      },
      showUpgrade: (r) => setReason(r ?? null),
      reason,
      closeUpgrade: () => setReason(undefined),
    };
  }, [info, local, refresh, reason]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// Außerhalb des Team-Bereichs (Gast-Ansicht) gibt es keinen Tarif: alles durchlassen
const OPEN: PlanCtx = {
  info: null,
  refresh: () => {},
  allow: () => true,
  more: () => true,
  allowFeature: () => true,
  allowUpload: () => true,
  showUpgrade: () => {},
  reason: undefined,
  closeUpgrade: () => {},
};

export function usePlan(): PlanCtx {
  return useContext(Ctx) ?? OPEN;
}
